import { FilesError } from "./types";
import {
  drawingSheetsSchema,
  issueListSchema,
  issueRecordSchema,
  openedIssueSchema,
  projectSheetsSchema,
  type IssuedRevisionsApi,
} from "./issues";

const UNEXPECTED = "The issued revisions sent something unexpected. Try again.";
const PDF_OPTIONS = {};

/** The issue calls on the doc service. `address` builds a drawing's URL (with the dev sign-in), `root` the URL of a route that names no drawing, `call` throws `FilesError`. */
export function createLiveIssues(
  address: (drawingId: string, rest: string) => string,
  call: (url: string, init: RequestInit) => Promise<Response>,
  root: (rest: string) => string,
): Required<IssuedRevisionsApi> {
  const issuePath = (issue: string, tail: string): string =>
    `/issues/${encodeURIComponent(issue)}${tail}`;
  return {
    async freezeIssue(_project, drawingId, body) {
      const response = await call(address(drawingId, "/issues"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(body),
      });
      const parsed = issueRecordSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data;
    },
    async drawingIssues(_project, drawingId) {
      const response = await call(address(drawingId, "/issues"), {
        headers: { Accept: "application/json" },
      });
      const parsed = issueListSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.issues;
    },
    async drawingSheets(_project, drawingId) {
      const response = await call(address(drawingId, "/sheets"), {
        headers: { Accept: "application/json" },
      });
      const parsed = drawingSheetsSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data;
    },
    async projectSheets(_project, drawingIds) {
      const response = await call(root("/sheets"), {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ drawings: drawingIds }),
      });
      const parsed = projectSheetsSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data.drawings;
    },
    async openIssuedRevision(_project, drawingId, issue) {
      const response = await call(address(drawingId, issuePath(issue, "/open")), {
        headers: { Accept: "application/json" },
      });
      const parsed = openedIssueSchema.safeParse(await response.json().catch(() => undefined));
      if (!parsed.success) throw new FilesError("unexpected", UNEXPECTED);
      return parsed.data;
    },
    issuedDxfUrl: (_project, drawingId, issue) => address(drawingId, issuePath(issue, "/dxf")),
    async issuedPdf(_project, drawingId, issue) {
      const response = await call(address(drawingId, issuePath(issue, "/pdf")), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(PDF_OPTIONS),
      });
      return response.blob();
    },
  };
}
